/**
 * Cross-platform Install prompt for the Field App.
 *
 * iOS Safari → shows a step-by-step "Share → Add to Home Screen" overlay (because iOS has no
 *              programmatic install API).
 * Android Chrome / Edge / Brave → captures the native `beforeinstallprompt` event and shows
 *              a one-tap "Install app" button that triggers the native install dialog.
 *
 * Renders nothing when already installed (display-mode standalone) or when the operator has
 * dismissed the prompt before.
 */
import { useEffect, useState } from "react";
import { Share, Plus, X, Download } from "lucide-react";

const STORAGE_KEY = "wilson:a2hs-dismissed";

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) ||
              (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return ios && safari;
}

function isStandalone() {
  return (typeof navigator !== "undefined" && navigator.standalone === true) ||
         (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches);
}

export default function AddToHomeScreenPrompt() {
  const [mode, setMode] = useState(null);          // "ios" | "android" | null
  const [deferred, setDeferred] = useState(null);  // captured beforeinstallprompt event (Android)

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    // iOS path — no event, just detect Safari and show instructions after a brief delay
    if (isIosSafari()) {
      const t = setTimeout(() => setMode("ios"), 800);
      return () => clearTimeout(t);
    }

    // Android (Chrome / Edge / Brave / Samsung Internet) — capture the install prompt event
    const onBeforeInstall = (e) => {
      e.preventDefault();          // stop the browser's own mini-infobar
      setDeferred(e);
      setMode("android");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Hide our prompt if the user installs it (any platform)
    const onInstalled = () => { setMode(null); setDeferred(null); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setMode(null);
  };

  const installAndroid = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    if (outcome === "accepted") setMode(null);
    else dismiss();
  };

  if (!mode) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[6000] px-4 pb-6 pointer-events-none"
         data-testid="a2hs-prompt">
      <div className="mx-auto max-w-md bg-white rounded-2xl shadow-2xl border-2 border-amber-300 overflow-hidden pointer-events-auto"
           style={{ animation: "slideUp .4s ease-out" }}>
        <style>{`@keyframes slideUp { from { transform: translateY(40px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
        <div className="bg-[#1a1a5c] text-white px-4 py-3 flex items-center gap-3">
          <img src="/icons/apple-touch-icon.png" alt="" className="w-10 h-10 rounded-lg shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">Install the Field App</div>
            <div className="font-display font-black text-base">
              {mode === "android" ? "One-tap install" : "Save to your home screen"}
            </div>
          </div>
          <button onClick={dismiss} data-testid="a2hs-close"
                  className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 shrink-0">
            <X size={18} />
          </button>
        </div>

        {mode === "ios" && (
          <>
            <ol className="p-4 space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-400 text-[#1a1a5c] font-black flex items-center justify-center text-xs shrink-0">1</span>
                <span className="flex-1">
                  Tap the <strong>Share</strong> button at the bottom of Safari
                  <Share size={14} className="inline-block ml-1 text-[#1a1a5c]" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-400 text-[#1a1a5c] font-black flex items-center justify-center text-xs shrink-0">2</span>
                <span className="flex-1">
                  Scroll down and tap <strong>Add to Home Screen</strong>
                  <Plus size={14} className="inline-block ml-1 text-[#1a1a5c]" />
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-400 text-[#1a1a5c] font-black flex items-center justify-center text-xs shrink-0">3</span>
                <span className="flex-1">Tap <strong>Add</strong> — the Field App will appear on your home screen.</span>
              </li>
            </ol>
            <div className="px-4 pb-4">
              <button onClick={dismiss} data-testid="a2hs-dismiss"
                      className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg py-2.5 text-sm transition-colors">
                Got it, don&apos;t show again
              </button>
            </div>
          </>
        )}

        {mode === "android" && (
          <div className="p-4 space-y-3">
            <p className="text-sm text-slate-700">
              Install the Field App on this device for fullscreen access and a home-screen icon.
              Works offline-friendly and keeps you signed in.
            </p>
            <button onClick={installAndroid} data-testid="a2hs-install"
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg py-3 text-sm flex items-center justify-center gap-2 transition-colors shadow">
              <Download size={16} /> Install app
            </button>
            <button onClick={dismiss} data-testid="a2hs-dismiss"
                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg py-2 text-sm transition-colors">
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
